import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { Html5Qrcode } from 'html5-qrcode';
import { createSubmission } from '../../api/runs';
import { ApiError } from '../../api/client';
import ErrorAlert from '../../components/ErrorAlert';
import type { ChallengeFormProps } from './TextChallengeForm';

const SCANNER_ID = 'qr-scanner-region';

const QrScanChallengeForm: React.FC<ChallengeFormProps> = ({
  orgSlug,
  runId,
  challenge,
  alreadySubmitted,
  onSubmitted,
  submitAction,
  initialContent,
}) => {
  const { t } = useTranslation('runs');
  const [scanning, setScanning] = useState(false);
  const [scannedValue, setScannedValue] = useState<string | null>(null);
  const [manualValue, setManualValue] = useState(
    typeof initialContent?.scanned_value === 'string' ? initialContent.scanned_value : '',
  );
  const [cameraError, setCameraError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const qrRef = useRef<Html5Qrcode | null>(null);

  useEffect(() => {
    return () => {
      if (qrRef.current) {
        qrRef.current.stop().catch((err) => console.debug('QR scanner cleanup failed:', err));
      }
    };
  }, []);

  const startScan = async () => {
    setCameraError(false);
    setScannedValue(null);
    setError(null);
    setScanning(true);
    try {
      const qr = new Html5Qrcode(SCANNER_ID);
      qrRef.current = qr;
      await qr.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: 250 },
        (decodedText) => {
          setScannedValue(decodedText);
          setScanning(false);
          qr.stop().catch((err) => console.debug('QR scanner stop failed:', err));
        },
        undefined,
      );
    } catch {
      setCameraError(true);
      setScanning(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const value = scannedValue ?? manualValue.trim();
    if (!value) return;
    setSubmitting(true);
    setError(null);
    try {
      const content = { scanned_value: value };
      const result = submitAction
        ? await submitAction(content)
        : await createSubmission(orgSlug, runId, {
            run_challenge_id: challenge.id,
            content,
          });
      onSubmitted(result);
    } catch (err) {
      setError(err instanceof ApiError ? err.detail : String(err));
      setSubmitting(false);
    }
  };

  if (alreadySubmitted) {
    return (
      <Typography variant="body2" sx={{
        color: "text.secondary"
      }}>
        {t('participant.challenge.alreadySubmitted')}
      </Typography>
    );
  }

  const submitValue = scannedValue ?? manualValue.trim();

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack spacing={2}>
        {error && <ErrorAlert message={error} />}
        {cameraError && (
          <ErrorAlert message={t('participant.challenge.qr.cameraError')} severity="warning" />
        )}

        {/* Scanner region — always mounted so Html5Qrcode can find the div */}
        <Box
          id={SCANNER_ID}
          aria-label={t('participant.challenge.qr.cameraViewport')}
          sx={{ width: '100%', minHeight: scanning ? 280 : 0 }}
        />

        {/* Hidden status region: announces scan result to screen readers */}
        <div aria-live="polite" role="status" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)' }}>
          {scannedValue ? t('participant.challenge.qr.scannedStatus', { code: scannedValue }) : ''}
        </div>

        {scannedValue ? (
          <Stack spacing={1}>
            <Typography variant="body2">
              {t('participant.challenge.qr.scanned')}: <strong>{scannedValue}</strong>
            </Typography>
            <Button variant="outlined" size="small" onClick={() => setScannedValue(null)}>
              {t('participant.challenge.qr.rescan')}
            </Button>
          </Stack>
        ) : (
          !scanning && (
            <Button variant="outlined" onClick={startScan} disabled={submitting}>
              {t('participant.challenge.qr.startScan')}
            </Button>
          )
        )}

        <Typography variant="caption" sx={{
          color: "text.secondary"
        }}>
          {t('participant.challenge.qr.scanInstruction')}
        </Typography>
        <TextField
          label={t('participant.challenge.qr.manualLabel')}
          fullWidth
          size="small"
          value={manualValue}
          onChange={(e) => setManualValue(e.target.value)}
          disabled={submitting || !!scannedValue}
        />

        <Button
          type="submit"
          variant="contained"
          disabled={!submitValue || submitting}
        >
          {t('participant.challenge.submit')}
        </Button>
      </Stack>
    </Box>
  );
};

export default QrScanChallengeForm;
